import type { MessageRenderer, RendererConfig, RendererResult, AnimationState } from './types';

/**
 * Renderer that displays content with progress bar animation
 */
export class ProgressRenderer implements MessageRenderer {
  render(
    _messageId: string,
    config: RendererConfig,
    currentState: AnimationState
  ): RendererResult {
    const steps = config.config?.progressSteps ?? [];
    const currentStep = currentState.currentStep ?? 0;
    const stepProgress = currentState.stepProgress ?? 0;

    if (currentStep >= steps.length) {
      return {
        displayContent: config.content,
        isComplete: true,
        progress: 100,
      };
    }

    const step = steps[currentStep];
    const totalSteps = steps.length;
    const baseProgress = (currentStep / totalSteps) * 100;
    const stepProgressPercent = (stepProgress / step.duration) * (100 / totalSteps);
    const progress = Math.min(100, baseProgress + stepProgressPercent);

    const progressBar = this.generateProgressBar(progress);
    const displayText = `${step.label}... ${progressBar} [${Math.round(progress)}%]`;

    return {
      displayContent: displayText,
      isComplete: false,
      progress,
    };
  }

  getInitialState(): AnimationState {
    return { currentStep: 0, stepProgress: 0 };
  }

  /**
   * Generates a simple text-based progress bar
   */
  private generateProgressBar(progress: number): string {
    const barLength = 20;
    const filled = Math.round((progress / 100) * barLength);
    const empty = barLength - filled;
    return '█'.repeat(filled) + '░'.repeat(empty);
  }
}

